import React, { useEffect, useMemo, useRef, useState } from 'react';
import { history, useLocation } from 'umi';
import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  InputNumber,
  Space,
  Spin,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, SyncOutlined } from '@ant-design/icons';
import {
  getMemberTrainingWorkspaceActionMetricTrend,
  getMemberTrainingWorkspaceActionPage,
  getMemberTrainingWorkspaceActionProfiles,
  getMemberTrainingWorkspaceCapabilityHistory,
  getMemberTrainingWorkspaceCourseRecords,
  getMemberTrainingWorkspaceEvidenceDetail,
  getMemberTrainingWorkspaceEvidencePage,
  getMemberTrainingWorkspaceProgressMonths,
  getMemberTrainingWorkspaceSnapshot,
  refreshMemberTrainingWorkspaceRecommendation,
} from '@/services/technical';
import { getCustomerList, relativesGetInfo } from '@/services/userManager';
import { loadActionNames } from './presentation';
import buildWorkspaceColumns from './SimpleWorkspaceColumns';
import SimpleWorkspaceDrawers from './SimpleWorkspaceDrawers';
import SimpleWorkspaceOverview from './SimpleWorkspaceOverview';
import SimpleWorkspaceTabs from './SimpleWorkspaceTabs';
import WorkspaceNavigation, { WORKSPACE_TAB_KEYS } from './WorkspaceNavigation';
import {
  ACTIVITY_TIMELINE_EVIDENCE_TYPES,
  DEFAULT_TABLE_PAGE_SIZE,
  DIMENSION_LABELS,
  PROFILE_FIELDS_ALREADY_SHOWN,
  TIMELINE_PAGE_SIZE,
  TRAINING_CONTEXT_CARDS,
  capabilityScore10,
  hasRecordedValue,
  memberDisplayName as resolveMemberDisplayName,
  normalizePhone,
  pageRequest,
  parseJson,
  questionnaireValue,
  readWorkspaceQueryState,
  sortProgressMonths,
  succeeded,
  timeText,
  writeWorkspaceQueryState,
} from './SimpleWorkspaceSupport';
import styles from './SimpleWorkspace.less';

const { Paragraph, Text, Title } = Typography;

export default function SimpleMemberTrainingWorkspace() {
  const location = useLocation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [memberIdentity, setMemberIdentity] = useState(null);
  const [memberPhone, setMemberPhone] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [actionRecords, setActionRecords] = useState([]);
  const [actionProfiles, setActionProfiles] = useState([]);
  const [actionNames, setActionNames] = useState({});
  const [courseRecords, setCourseRecords] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionProfilesLoading, setActionProfilesLoading] = useState(false);
  const [courseLoading, setCourseLoading] = useState(false);
  const [communicationLoading, setCommunicationLoading] = useState(false);
  const [monthlyProgressLoading, setMonthlyProgressLoading] = useState(false);
  const [actionProfileCurrent, setActionProfileCurrent] = useState(1);
  const [courseCurrent, setCourseCurrent] = useState(1);
  const [communicationCurrent, setCommunicationCurrent] = useState(1);
  const [actionPage, setActionPage] = useState({
    current: 1,
    pageSize: DEFAULT_TABLE_PAGE_SIZE,
    total: 0,
  });
  const [coursePage, setCoursePage] = useState({
    hasMore: false,
    nextBeforeStartTime: null,
    nextBeforeRoomId: null,
  });
  const [communicationEvidence, setCommunicationEvidence] = useState([]);
  const [communicationPage, setCommunicationPage] = useState({
    hasMore: false,
    nextCursor: null,
  });
  const [timelineEvidence, setTimelineEvidence] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineLoaded, setTimelineLoaded] = useState(false);
  const [timelinePage, setTimelinePage] = useState({
    hasMore: false,
    nextCursor: null,
  });
  const [timelineDetailVisible, setTimelineDetailVisible] = useState(false);
  const [timelineDetailLoading, setTimelineDetailLoading] = useState(false);
  const [selectedTimelineEvent, setSelectedTimelineEvent] = useState(null);
  const [timelineDetail, setTimelineDetail] = useState(null);
  const [monthlyProgress, setMonthlyProgress] = useState([]);
  const [unavailableMonthOffsets, setUnavailableMonthOffsets] = useState([]);
  const [capabilityHistory, setCapabilityHistory] = useState([]);
  const [capabilityHistoryTitle, setCapabilityHistoryTitle] = useState('');
  const [capabilityHistoryVisible, setCapabilityHistoryVisible] =
    useState(false);
  const [capabilityHistoryLoading, setCapabilityHistoryLoading] =
    useState(false);
  const [actionProfileVisible, setActionProfileVisible] = useState(false);
  const [selectedActionProfile, setSelectedActionProfile] = useState(null);
  const [actionMetricTrend, setActionMetricTrend] = useState(null);
  const [selectedMetricSeries, setSelectedMetricSeries] = useState(null);
  const [actionMetricLoading, setActionMetricLoading] = useState(false);
  const actionMetricRequestSequence = useRef(0);
  const capabilityHistoryRequestSequence = useRef(0);
  const timelineDetailRequestSequence = useRef(0);
  const workspaceRequestSequence = useRef(0);
  const activeMemberId = useRef(null);
  const workspaceStateRestored = useRef(false);

  const profile = snapshot?.profile;
  const memberDisplayName = resolveMemberDisplayName(memberIdentity);
  const currentRecommendation = snapshot?.currentRecommendation;
  const currentProblems = currentRecommendation?.mainProblems || [];
  const capabilityAccounts = snapshot?.capabilityAccounts || [];
  const trainingContextCards = useMemo(
    () =>
      TRAINING_CONTEXT_CARDS.map((definition) => {
        const source = snapshot?.trainingStatistics?.[definition.key] || {};
        const indicators = (source.indicators || []).filter(
          (item) =>
            item?.value !== null &&
            item?.value !== undefined &&
            item?.value !== '',
        );
        const dailyStats = Array.isArray(source.dailyStats)
          ? source.dailyStats.slice(-7).map((value) => Number(value) || 0)
          : [];
        return { ...definition, indicators, dailyStats };
      }),
    [snapshot],
  );
  const memberBasicValues = {
    gender: questionnaireValue(profile, '性别'),
    age: questionnaireValue(profile, '年龄'),
    height: questionnaireValue(profile, '身高'),
    weight: questionnaireValue(profile, '体重'),
  };
  const additionalProfileFields = [
    ...new Map(
      (profile?.questionnaireList || [])
        .filter(
          (item) => item?.key && !PROFILE_FIELDS_ALREADY_SHOWN.has(item.key),
        )
        .map((item) => [item.key, item]),
    ).values(),
  ];
  const radarData = useMemo(
    () =>
      capabilityAccounts
        .filter((item) => capabilityScore10(item) !== null)
        .map((item) => ({
          dimension: DIMENSION_LABELS[item.dimensionCode] || '其他能力',
          score: capabilityScore10(item),
        })),
    [capabilityAccounts],
  );
  const evaluatedCapabilityCount = capabilityAccounts.filter(
    (item) => capabilityScore10(item) !== null,
  ).length;
  const weeklyTrainingData = useMemo(() => {
    const dayLabels = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];
    const courseValues =
      snapshot?.trainingStatistics?.training?.dailyStats || [];
    const sportValues = snapshot?.trainingStatistics?.sports?.dailyStats || [];
    return dayLabels.reduce((result, day, index) => {
      result.push({
        day,
        type: '课程训练',
        minutes: Number(courseValues[index]) || 0,
      });
      result.push({
        day,
        type: '运动记录',
        minutes: Number(sportValues[index]) || 0,
      });
      return result;
    }, []);
  }, [snapshot]);
  const hasWeeklyTraining = weeklyTrainingData.some((item) => item.minutes > 0);
  const weeklyTrainingSummary = useMemo(() => {
    const byDay = weeklyTrainingData.reduce((result, item) => {
      result[item.day] = (result[item.day] || 0) + item.minutes;
      return result;
    }, {});
    const courseMinutes = weeklyTrainingData
      .filter((item) => item.type === '课程训练')
      .reduce((sum, item) => sum + item.minutes, 0);
    const sportMinutes = weeklyTrainingData
      .filter((item) => item.type === '运动记录')
      .reduce((sum, item) => sum + item.minutes, 0);
    return {
      courseMinutes,
      sportMinutes,
      activeDays: Object.values(byDay).filter((value) => value > 0).length,
    };
  }, [weeklyTrainingData]);
  const bodyMetricTrends = snapshot?.bodyMetricTrends?.metrics || [];
  const weightTrendData = useMemo(() => {
    const metric = bodyMetricTrends.find(
      (item) => item.metricCode === 'weight',
    );
    return (metric?.points || []).map((point) => ({
      date: timeText(point.occurredAt),
      value: Number(point.value),
    }));
  }, [bodyMetricTrends]);
  const circumferenceTrendData = useMemo(
    () =>
      bodyMetricTrends
        .filter((item) => item.metricCode !== 'weight')
        .reduce((result, metric) => {
          (metric.points || []).forEach((point) => {
            result.push({
              date: timeText(point.occurredAt),
              metric: metric.metricName,
              value: Number(point.value),
            });
          });
          return result;
        }, []),
    [bodyMetricTrends],
  );
  const monthlyTrainingTrend = useMemo(
    () =>
      sortProgressMonths(monthlyProgress).reduce((result, item) => {
        result.push({
          month: item.month || '-',
          type: '全部训练',
          minutes: Number(item.totalSportMinute) || 0,
        });
        result.push({
          month: item.month || '-',
          type: '自主运动',
          minutes: Number(item.customizedSportMinutes) || 0,
        });
        return result;
      }, []),
    [monthlyProgress],
  );
  const monthlyTrainingSummary = useMemo(
    () =>
      monthlyProgress.reduce(
        (result, item) => ({
          minutes: result.minutes + (Number(item.totalSportMinute) || 0),
          days: result.days + (Number(item.totalDailyDay) || 0),
          sessions: result.sessions + (Number(item.totalSportCount) || 0),
        }),
        { minutes: 0, days: 0, sessions: 0 },
      ),
    [monthlyProgress],
  );
  const monthlyProgressSorted = useMemo(
    () => sortProgressMonths(monthlyProgress),
    [monthlyProgress],
  );
  const latestMonth =
    monthlyProgressSorted[monthlyProgressSorted.length - 1] || {};
  const previousMonth =
    monthlyProgressSorted[monthlyProgressSorted.length - 2] || {};
  const availableTrainingContextCards = useMemo(
    () =>
      trainingContextCards.filter(
        (item) =>
          item.indicators.some((indicator) =>
            hasRecordedValue(indicator.value),
          ) || item.dailyStats.some((value) => value > 0),
      ),
    [trainingContextCards],
  );
  const courseTitles = useMemo(
    () =>
      courseRecords.reduce((result, item) => {
        if (item.roomId && item.title) result[item.roomId] = item.title;
        return result;
      }, {}),
    [courseRecords],
  );
  const capabilityHistoryChart = useMemo(
    () =>
      capabilityHistory
        .filter((item) => capabilityScore10(item) !== null)
        .slice()
        .sort(
          (left, right) =>
            (left.windowEndedAt || left.ctime || 0) -
            (right.windowEndedAt || right.ctime || 0),
        )
        .reduce((result, item) => {
          const date = timeText(item.windowEndedAt || item.ctime);
          result.push({
            date,
            score: capabilityScore10(item),
            series: '当前估计',
          });
          if (
            item.confirmedScore !== null &&
            item.confirmedScore !== undefined
          ) {
            result.push({
              date,
              score: Number(item.confirmedScore) / 10,
              series: '训练确认',
            });
          }
          return result;
        }, []),
    [capabilityHistory],
  );
  const actionMetricChart = useMemo(
    () =>
      (selectedMetricSeries?.points || []).map((item) => ({
        date: timeText(item.observedAt),
        value: item.value,
      })),
    [selectedMetricSeries],
  );
  const communicationRows = useMemo(
    () =>
      communicationEvidence.map((item) => ({
        ...item,
        ...(parseJson(item.normalizedContent) || {}),
      })),
    [communicationEvidence],
  );
  const communicationSourceCounts = useMemo(
    () =>
      communicationRows.reduce(
        (result, item) => ({
          ...result,
          [item.sourceType]: (result[item.sourceType] || 0) + 1,
        }),
        {},
      ),
    [communicationRows],
  );

  const loadActionPage = async (
    memberId,
    currentPage = 1,
    pageSize = DEFAULT_TABLE_PAGE_SIZE,
    requestSequence = workspaceRequestSequence.current,
  ) => {
    setActionLoading(true);
    try {
      const response = await getMemberTrainingWorkspaceActionPage(
        pageRequest(memberId, currentPage, pageSize),
      );
      if (requestSequence !== workspaceRequestSequence.current) return;
      if (!succeeded(response)) {
        setActionRecords([]);
        message.warning(response?.msg || '动作观察查询失败');
        return;
      }
      const records = response.data || [];
      setActionRecords(records);
      loadActionNames(records.map((item) => item.actionId)).then((names) => {
        if (
          requestSequence === workspaceRequestSequence.current &&
          activeMemberId.current === memberId
        ) {
          setActionNames((previous) => ({ ...previous, ...names }));
        }
      });
      setActionPage({
        current: currentPage,
        pageSize,
        total: response.page?.count || 0,
      });
    } catch (error) {
      if (requestSequence !== workspaceRequestSequence.current) return;
      setActionRecords([]);
      message.warning('动作观察暂时不可用');
    } finally {
      if (requestSequence === workspaceRequestSequence.current) {
        setActionLoading(false);
      }
    }
  };

  const loadActionProfiles = async (
    memberId,
    requestSequence = workspaceRequestSequence.current,
  ) => {
    setActionProfilesLoading(true);
    setActionProfileCurrent(1);
    try {
      const response = await getMemberTrainingWorkspaceActionProfiles({
        requestData: { memberId },
      });
      if (requestSequence !== workspaceRequestSequence.current) return;
      if (!succeeded(response)) {
        setActionProfiles([]);
        message.warning(response?.msg || '动作画像查询失败');
        return;
      }
      const profiles = response.data || [];
      setActionProfiles(profiles);
      loadActionNames(profiles.map((item) => item.actionId)).then((names) => {
        if (
          requestSequence === workspaceRequestSequence.current &&
          activeMemberId.current === memberId
        ) {
          setActionNames((previous) => ({ ...previous, ...names }));
        }
      });
    } catch (error) {
      if (requestSequence !== workspaceRequestSequence.current) return;
      setActionProfiles([]);
      message.warning('动作画像暂时不可用');
    } finally {
      if (requestSequence === workspaceRequestSequence.current) {
        setActionProfilesLoading(false);
      }
    }
  };

  const loadCoursePage = async (
    memberId,
    append = false,
    requestSequence = workspaceRequestSequence.current,
    targetPage = 1,
  ) => {
    setCourseLoading(true);
    try {
      const response = await getMemberTrainingWorkspaceCourseRecords({
        requestData: {
          memberId,
          limit: DEFAULT_TABLE_PAGE_SIZE,
          beforeStartTime: append ? coursePage.nextBeforeStartTime : null,
          beforeRoomId: append ? coursePage.nextBeforeRoomId : null,
        },
      });
      if (requestSequence !== workspaceRequestSequence.current) return;
      if (!succeeded(response)) {
        if (!append) {
          setCourseRecords([]);
          setCourseCurrent(1);
          setCoursePage({
            hasMore: false,
            nextBeforeStartTime: null,
            nextBeforeRoomId: null,
          });
        }
        message.warning(response?.msg || '课程观察查询失败');
        return;
      }
      const items = response.data?.items || [];
      setCourseRecords((previous) =>
        append ? [...previous, ...items] : items,
      );
      if (!append) {
        setCourseCurrent(1);
      } else if (items.length) {
        setCourseCurrent(targetPage);
      }
      setCoursePage({
        hasMore: Boolean(response.data?.hasMore),
        nextBeforeStartTime: response.data?.nextBeforeStartTime || null,
        nextBeforeRoomId: response.data?.nextBeforeRoomId || null,
      });
    } catch (error) {
      if (requestSequence !== workspaceRequestSequence.current) return;
      if (!append) {
        setCourseRecords([]);
        setCourseCurrent(1);
        setCoursePage({
          hasMore: false,
          nextBeforeStartTime: null,
          nextBeforeRoomId: null,
        });
      }
      message.warning('课程观察暂时不可用');
    } finally {
      if (requestSequence === workspaceRequestSequence.current) {
        setCourseLoading(false);
      }
    }
  };

  const loadCommunicationPage = async (
    memberId,
    append = false,
    requestSequence = workspaceRequestSequence.current,
    targetPage = 1,
  ) => {
    setCommunicationLoading(true);
    try {
      const response = await getMemberTrainingWorkspaceEvidencePage({
        requestData: {
          memberId,
          evidenceType: 'communication_observation',
          beforeCursor: append ? communicationPage.nextCursor : undefined,
          limit: DEFAULT_TABLE_PAGE_SIZE,
        },
      });
      if (requestSequence !== workspaceRequestSequence.current) return;
      if (!succeeded(response)) {
        if (!append) {
          setCommunicationEvidence([]);
          setCommunicationCurrent(1);
          setCommunicationPage({ hasMore: false, nextCursor: null });
        }
        message.warning(response?.msg || '训练沟通观察查询失败');
        return;
      }
      const items = response.data?.items || [];
      setCommunicationEvidence((previous) =>
        append ? [...previous, ...items] : items,
      );
      if (!append) {
        setCommunicationCurrent(1);
      } else if (items.length) {
        setCommunicationCurrent(targetPage);
      }
      setCommunicationPage({
        hasMore: Boolean(response.data?.hasMore && response.data?.nextCursor),
        nextCursor: response.data?.nextCursor || null,
      });
    } catch (error) {
      if (requestSequence !== workspaceRequestSequence.current) return;
      if (!append) {
        setCommunicationEvidence([]);
        setCommunicationCurrent(1);
        setCommunicationPage({ hasMore: false, nextCursor: null });
      }
      message.warning('训练沟通观察暂时不可用');
    } finally {
      if (requestSequence === workspaceRequestSequence.current) {
        setCommunicationLoading(false);
      }
    }
  };

  const loadTimelinePage = async (
    memberId,
    append = false,
    requestSequence = workspaceRequestSequence.current,
  ) => {
    setTimelineLoading(true);
    try {
      const response = await getMemberTrainingWorkspaceEvidencePage({
        requestData: {
          memberId,
          evidenceTypes: ACTIVITY_TIMELINE_EVIDENCE_TYPES,
          beforeCursor: append ? timelinePage.nextCursor : undefined,
          limit: TIMELINE_PAGE_SIZE,
        },
      });
      if (requestSequence !== workspaceRequestSequence.current) return;
      if (!succeeded(response)) {
        if (!append) {
          setTimelineEvidence([]);
          setTimelinePage({ hasMore: false, nextCursor: null });
        }
        message.warning(response?.msg || '会员活动时间轴查询失败');
        return;
      }
      const items = response.data?.items || [];
      const visibleItems = items.filter((item) => {
        if (item?.evidenceType !== 'member_goal_change') return true;
        return (
          parseJson(item.normalizedContent)?.eventType !== 'baseline_recorded'
        );
      });
      setTimelineLoaded(true);
      setTimelineEvidence((previous) => {
        const merged = append ? [...previous, ...visibleItems] : visibleItems;
        const unique = new Map();
        merged.forEach((item) => {
          const eventKey = item?.sourceRef || item?.evidenceId;
          // 同一个业务来源可能因历史同步缺陷产生多个证据版本；时间线只展示最新一条。
          if (eventKey && !unique.has(eventKey)) unique.set(eventKey, item);
        });
        return Array.from(unique.values());
      });
      setTimelinePage({
        hasMore: Boolean(response.data?.hasMore && response.data?.nextCursor),
        nextCursor: response.data?.nextCursor || null,
      });
    } catch (error) {
      if (requestSequence !== workspaceRequestSequence.current) return;
      if (!append) {
        setTimelineEvidence([]);
        setTimelinePage({ hasMore: false, nextCursor: null });
      }
      message.warning('会员活动时间轴暂时不可用');
    } finally {
      if (requestSequence === workspaceRequestSequence.current) {
        setTimelineLoading(false);
      }
    }
  };

  const loadMonthlyProgress = async (
    memberId,
    requestSequence = workspaceRequestSequence.current,
  ) => {
    setMonthlyProgressLoading(true);
    try {
      const response = await getMemberTrainingWorkspaceProgressMonths({
        requestData: { memberId, months: 6 },
      });
      if (requestSequence !== workspaceRequestSequence.current) return;
      if (!succeeded(response)) {
        setMonthlyProgress([]);
        setUnavailableMonthOffsets(
          Array.from({ length: 6 }, (_, monthOffset) => monthOffset),
        );
        return;
      }
      setMonthlyProgress(response.data?.months || []);
      setUnavailableMonthOffsets(response.data?.unavailableMonthOffsets || []);
    } catch (error) {
      if (requestSequence !== workspaceRequestSequence.current) return;
      setMonthlyProgress([]);
      setUnavailableMonthOffsets(
        Array.from({ length: 6 }, (_, monthOffset) => monthOffset),
      );
    } finally {
      if (requestSequence === workspaceRequestSequence.current) {
        setMonthlyProgressLoading(false);
      }
    }
  };

  const resolveMemberByPhone = async (phone) => {
    const response = await getCustomerList({
      requestData: { phone },
      page: { currentPage: 1, pageSize: DEFAULT_TABLE_PAGE_SIZE },
    });
    if (!succeeded(response)) {
      throw new Error(response?.msg || '手机号查询失败');
    }
    const member = (response.data || []).find(
      (item) => normalizePhone(item.phone) === phone,
    );
    if (!member?.userId) {
      throw new Error('未找到该手机号对应的会员');
    }
    return member;
  };

  const loadMemberIdentity = async (
    memberId,
    requestSequence = workspaceRequestSequence.current,
  ) => {
    try {
      const response = await relativesGetInfo({
        requestData: { userId: memberId, relationId: 0 },
      });
      if (
        requestSequence !== workspaceRequestSequence.current ||
        activeMemberId.current !== memberId
      )
        return;
      if (!succeeded(response)) {
        setMemberIdentity(null);
        return;
      }
      setMemberIdentity({
        wxNickname: response.data?.wxNickname,
        nickname: response.data?.nickname,
      });
    } catch (error) {
      if (
        requestSequence === workspaceRequestSequence.current &&
        activeMemberId.current === memberId
      ) {
        setMemberIdentity(null);
      }
    }
  };

  const load = async (providedValues, tabKey = activeTab) => {
    const { memberId: inputMemberId, phone: inputPhone } =
      providedValues || (await form.validateFields());
    const phone = normalizePhone(inputPhone);
    if (!inputMemberId && !phone) {
      message.warning('请输入会员 ID 或手机号');
      return;
    }
    if (inputMemberId && phone) {
      message.warning('会员 ID 和手机号请任选一种查询方式');
      return;
    }
    if (phone && !/^1\d{10}$/.test(phone)) {
      message.warning('请输入正确的 11 位手机号');
      return;
    }
    const requestSequence = workspaceRequestSequence.current + 1;
    workspaceRequestSequence.current = requestSequence;
    activeMemberId.current = null;
    setSnapshot(null);
    setMemberIdentity(null);
    setMemberPhone('');
    setActionNames({});
    setActionRecords([]);
    setActionProfiles([]);
    setCourseRecords([]);
    setCommunicationEvidence([]);
    setTimelineEvidence([]);
    setTimelineLoaded(false);
    setMonthlyProgress([]);
    setUnavailableMonthOffsets([]);
    setActionLoading(false);
    setActionProfilesLoading(false);
    setCourseLoading(false);
    setCommunicationLoading(false);
    setTimelineLoading(false);
    setMonthlyProgressLoading(false);
    setActionProfileCurrent(1);
    setCourseCurrent(1);
    setCommunicationCurrent(1);
    setCoursePage({
      hasMore: false,
      nextBeforeStartTime: null,
      nextBeforeRoomId: null,
    });
    setCommunicationPage({ hasMore: false, nextCursor: null });
    setTimelinePage({ hasMore: false, nextCursor: null });
    actionMetricRequestSequence.current += 1;
    setActionProfileVisible(false);
    setSelectedActionProfile(null);
    setActionMetricTrend(null);
    setSelectedMetricSeries(null);
    setActionMetricLoading(false);
    capabilityHistoryRequestSequence.current += 1;
    setCapabilityHistoryVisible(false);
    setCapabilityHistoryTitle('');
    setCapabilityHistory([]);
    setCapabilityHistoryLoading(false);
    setTimelineDetailVisible(false);
    timelineDetailRequestSequence.current += 1;
    setSelectedTimelineEvent(null);
    setTimelineDetail(null);
    setLoading(true);
    try {
      const resolvedMember = inputMemberId
        ? { userId: inputMemberId, phone: '' }
        : await resolveMemberByPhone(phone);
      if (requestSequence !== workspaceRequestSequence.current) return;
      const memberId = resolvedMember.userId;
      activeMemberId.current = memberId;
      setMemberPhone(resolvedMember.phone || phone || '');
      writeWorkspaceQueryState({
        memberId: inputMemberId || null,
        phone: inputMemberId ? '' : phone,
        activeTab: tabKey || 'overview',
      });
      // 月度趋势独立加载，避免 Ptainer 多月聚合拖慢会员基础档案首屏。
      loadMonthlyProgress(memberId, requestSequence);
      // 活动时间轴独立加载：首页先展示档案，近期活动随后补齐。
      loadTimelinePage(memberId, false, requestSequence);
      // 昵称复用客户档案接口独立加载，失败不阻断训练工作台主数据。
      loadMemberIdentity(memberId, requestSequence);
      const [snapshotResponse] = await Promise.all([
        getMemberTrainingWorkspaceSnapshot({ requestData: { memberId } }),
        loadActionPage(memberId, 1, DEFAULT_TABLE_PAGE_SIZE, requestSequence),
        loadActionProfiles(memberId, requestSequence),
        loadCoursePage(memberId, false, requestSequence),
        loadCommunicationPage(memberId, false, requestSequence),
      ]);
      if (requestSequence !== workspaceRequestSequence.current) return;
      if (!succeeded(snapshotResponse)) {
        activeMemberId.current = null;
        setSnapshot(null);
        message.error(snapshotResponse?.msg || '会员训练数据查询失败');
        return;
      }
      setSnapshot(snapshotResponse.data || null);
    } catch (error) {
      if (requestSequence !== workspaceRequestSequence.current) return;
      activeMemberId.current = null;
      setSnapshot(null);
      message.error(error?.message || '会员训练数据暂时不可用');
    } finally {
      if (requestSequence === workspaceRequestSequence.current) {
        setLoading(false);
      }
    }
  };

  const refreshLatestData = async () => {
    if (!snapshot?.memberId || refreshing) return;
    setRefreshing(true);
    try {
      const response = await refreshMemberTrainingWorkspaceRecommendation({
        requestData: { memberId: snapshot.memberId },
      });
      if (!succeeded(response)) {
        message.error(response?.msg || '最新训练数据同步失败');
        return;
      }
      await load(form.getFieldsValue(), activeTab);
      const journeyStatus = (response.data?.sourceStatuses || []).find(
        (item) => item?.source === 'journey_fact_sync',
      );
      const journeySync = response.data?.journeyFactSync;
      if (journeyStatus?.state === 'unavailable' || !journeySync) {
        message.warning(
          `其他训练数据已刷新，但运动与打卡同步失败：${
            journeyStatus?.message || '旅程数据源暂时不可用'
          }`,
        );
      } else {
        const scanned = journeySync.scannedCount || 0;
        const activities = journeySync.activityFactCount || 0;
        const contexts = journeySync.contextFactCount || 0;
        message.success(
          `同步完成：扫描 ${scanned} 条旅程，运动 ${activities} 条，身体/生活记录 ${contexts} 条`,
        );
      }
    } catch (error) {
      message.error('最新训练数据同步失败');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (workspaceStateRestored.current) return;
    workspaceStateRestored.current = true;
    const queryMemberId = Number(location.query?.memberId);
    if (Number.isSafeInteger(queryMemberId) && queryMemberId > 0) {
      const queryValues = { memberId: queryMemberId, phone: undefined };
      form.setFieldsValue(queryValues);
      load(queryValues, 'overview');
      return;
    }
    const saved = readWorkspaceQueryState();
    if (!saved?.memberId && !saved?.phone) return;
    const restoredTab = WORKSPACE_TAB_KEYS.has(saved.activeTab)
      ? saved.activeTab
      : 'overview';
    const restoredValues = {
      memberId: saved.memberId || undefined,
      phone: saved.phone || undefined,
    };
    setActiveTab(restoredTab);
    form.setFieldsValue(restoredValues);
    load(restoredValues, restoredTab);
  }, []);

  const changeActiveTab = (key) => {
    const nextKey = WORKSPACE_TAB_KEYS.has(key) ? key : 'overview';
    setActiveTab(nextKey);
    const saved = readWorkspaceQueryState();
    if (saved) {
      writeWorkspaceQueryState({ ...saved, activeTab: nextKey });
    }
    if (
      nextKey === 'archive' &&
      snapshot?.memberId &&
      !timelineLoaded &&
      !timelineLoading
    ) {
      loadTimelinePage(
        snapshot.memberId,
        false,
        workspaceRequestSequence.current,
      );
    }
  };

  const openTimelineEvent = async (record) => {
    const requestSequence = timelineDetailRequestSequence.current + 1;
    timelineDetailRequestSequence.current = requestSequence;
    const memberId = snapshot?.memberId;
    setSelectedTimelineEvent(record);
    setTimelineDetail(record);
    setTimelineDetailVisible(true);
    if (!memberId || !record?.evidenceId) return;
    setTimelineDetailLoading(true);
    try {
      const response = await getMemberTrainingWorkspaceEvidenceDetail({
        requestData: {
          memberId,
          evidenceId: record.evidenceId,
        },
      });
      if (
        requestSequence !== timelineDetailRequestSequence.current ||
        activeMemberId.current !== memberId
      )
        return;
      if (!succeeded(response) || !response.data) {
        message.warning(response?.msg || '活动详情查询失败');
        return;
      }
      setTimelineDetail(response.data);
    } catch (error) {
      if (requestSequence === timelineDetailRequestSequence.current) {
        message.warning('活动详情暂时不可用');
      }
    } finally {
      if (requestSequence === timelineDetailRequestSequence.current) {
        setTimelineDetailLoading(false);
      }
    }
  };

  const closeTimelineDetail = () => {
    timelineDetailRequestSequence.current += 1;
    setTimelineDetailVisible(false);
    setTimelineDetailLoading(false);
  };

  const changeCoursePage = (nextPage) => {
    const loadedPageCount = Math.max(
      1,
      Math.ceil(courseRecords.length / DEFAULT_TABLE_PAGE_SIZE),
    );
    if (nextPage <= loadedPageCount) {
      setCourseCurrent(nextPage);
      return;
    }
    if (coursePage.hasMore && !courseLoading) {
      loadCoursePage(
        snapshot.memberId,
        true,
        workspaceRequestSequence.current,
        nextPage,
      );
    }
  };

  const changeCommunicationPage = (nextPage) => {
    const loadedPageCount = Math.max(
      1,
      Math.ceil(communicationEvidence.length / DEFAULT_TABLE_PAGE_SIZE),
    );
    if (nextPage <= loadedPageCount) {
      setCommunicationCurrent(nextPage);
      return;
    }
    if (communicationPage.hasMore && !communicationLoading) {
      loadCommunicationPage(
        snapshot.memberId,
        true,
        workspaceRequestSequence.current,
        nextPage,
      );
    }
  };

  const openActionProfile = async (record) => {
    const requestSequence = actionMetricRequestSequence.current + 1;
    actionMetricRequestSequence.current = requestSequence;
    const memberId = snapshot?.memberId;
    if (!memberId || !record?.actionId) return;
    setSelectedActionProfile(record);
    setActionProfileVisible(true);
    setActionMetricTrend(null);
    setSelectedMetricSeries(null);
    setActionMetricLoading(true);
    try {
      const response = await getMemberTrainingWorkspaceActionMetricTrend({
        requestData: {
          memberId,
          actionId: record.actionId,
          limit: 20,
        },
      });
      if (
        actionMetricRequestSequence.current !== requestSequence ||
        activeMemberId.current !== memberId
      )
        return;
      if (!succeeded(response)) {
        message.error(response?.msg || '动作指标历史查询失败');
        return;
      }
      const trend = response.data || null;
      setActionMetricTrend(trend);
      setSelectedMetricSeries(trend?.metricSeries?.[0] || null);
    } catch (error) {
      if (
        actionMetricRequestSequence.current === requestSequence &&
        activeMemberId.current === memberId
      ) {
        message.error('动作指标历史暂时不可用');
      }
    } finally {
      if (
        actionMetricRequestSequence.current === requestSequence &&
        activeMemberId.current === memberId
      ) {
        setActionMetricLoading(false);
      }
    }
  };

  const closeActionProfile = () => {
    actionMetricRequestSequence.current += 1;
    setActionProfileVisible(false);
    setActionMetricLoading(false);
  };

  const openActionDetail = (record) => {
    const saved = readWorkspaceQueryState();
    if (saved) {
      writeWorkspaceQueryState({ ...saved, activeTab });
    }
    history.push({
      pathname: '/technical/memberTrainingWorkspace',
      query: {
        view: 'actionDetail',
        memberId: snapshot.memberId,
        evaluationId: record.evaluationId,
      },
    });
  };

  const openCourseDetail = (record) => {
    const saved = readWorkspaceQueryState();
    if (saved) {
      writeWorkspaceQueryState({ ...saved, activeTab });
    }
    history.push({
      pathname: '/technical/memberTrainingWorkspace',
      query: {
        view: 'courseDetail',
        memberId: snapshot.memberId,
        roomId: record.roomId,
      },
    });
  };

  const openCapabilityHistory = async (record) => {
    const requestSequence = capabilityHistoryRequestSequence.current + 1;
    capabilityHistoryRequestSequence.current = requestSequence;
    const memberId = snapshot?.memberId;
    if (!memberId || !record?.dimensionCode) return;
    setCapabilityHistoryVisible(true);
    setCapabilityHistoryTitle(
      `${DIMENSION_LABELS[record.dimensionCode] || '运动能力'}变化记录`,
    );
    setCapabilityHistory([]);
    setCapabilityHistoryLoading(true);
    try {
      const response = await getMemberTrainingWorkspaceCapabilityHistory({
        requestData: {
          memberId,
          dimensionCode: record.dimensionCode,
          limit: 100,
        },
      });
      if (
        requestSequence !== capabilityHistoryRequestSequence.current ||
        activeMemberId.current !== memberId
      )
        return;
      if (!succeeded(response)) {
        message.error(response?.msg || '运动能力变化记录查询失败');
        return;
      }
      setCapabilityHistory(response.data || []);
    } catch (error) {
      if (
        requestSequence === capabilityHistoryRequestSequence.current &&
        activeMemberId.current === memberId
      ) {
        message.error('运动能力变化记录暂时不可用');
      }
    } finally {
      if (
        requestSequence === capabilityHistoryRequestSequence.current &&
        activeMemberId.current === memberId
      ) {
        setCapabilityHistoryLoading(false);
      }
    }
  };

  const closeCapabilityHistory = () => {
    capabilityHistoryRequestSequence.current += 1;
    setCapabilityHistoryVisible(false);
    setCapabilityHistoryLoading(false);
  };

  const workspaceColumns = buildWorkspaceColumns({
    actionNames,
    courseTitles,
    openActionDetail,
    openActionProfile,
    openCourseDetail,
    openCapabilityHistory,
    setSelectedMetricSeries,
  });

  return (
    <Space
      direction="vertical"
      size={20}
      className={`${styles.workspace} ${styles.workspaceContent}`}
    >
      <Card bordered={false} className={styles.heroCard}>
        <div className={styles.heroHeading}>
          <div>
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              className={styles.archiveBackButton}
              onClick={() => history.push('/technical/memberTrainingWorkspace')}
            >
              返回会员列表
            </Button>
            <Text className={styles.eyebrow}>POWER MOVE</Text>
            <Title level={2} className={styles.pageTitle}>
              会员训练数据中心
            </Title>
            <Paragraph type="secondary" className={styles.pageDescription}>
              汇聚训练事实、动作与课程观察、运动能力和系统判断。
            </Paragraph>
          </div>
        </div>
        <div className={styles.searchPanel}>
          <Form
            form={form}
            layout="inline"
            onFinish={load}
            className={styles.searchForm}
          >
            <Form.Item name="memberId" label="会员 ID">
              <InputNumber
                min={1}
                precision={0}
                controls={false}
                placeholder="请输入会员 ID"
                className={styles.searchInput}
                onChange={(value) => {
                  if (value) form.setFieldsValue({ phone: undefined });
                }}
              />
            </Form.Item>
            <Text type="secondary" className={styles.searchSeparator}>
              或
            </Text>
            <Form.Item name="phone" label="手机号">
              <Input
                allowClear
                maxLength={20}
                placeholder="请输入 11 位手机号"
                className={styles.searchInput}
                onChange={(event) => {
                  if (event.target.value) {
                    form.setFieldsValue({ memberId: undefined });
                  }
                }}
              />
            </Form.Item>
            <Form.Item className={styles.searchAction}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className={styles.searchButton}
              >
                查询会员
              </Button>
            </Form.Item>
          </Form>
          <div className={styles.searchMeta}>
            <Text type="secondary" className={styles.searchHint}>
              会员 ID 与手机号任选一种查询方式
            </Text>
            {snapshot && (
              <Button
                icon={<SyncOutlined />}
                loading={refreshing}
                onClick={refreshLatestData}
                className={styles.syncButton}
              >
                同步最新数据
              </Button>
            )}
          </div>
        </div>
      </Card>

      {loading && !snapshot && (
        <Card bordered={false} className={styles.stateCard}>
          <Spin />
        </Card>
      )}
      {!loading && !snapshot && (
        <Card bordered={false} className={styles.stateCard}>
          <Empty description="输入会员 ID 或手机号后查看训练档案" />
        </Card>
      )}

      {snapshot && (
        <div className={styles.workspaceFrame}>
          <WorkspaceNavigation
            activeKey={activeTab}
            profile={profile}
            memberDisplayName={memberDisplayName}
            memberId={snapshot.memberId}
            onChange={changeActiveTab}
          />
          <main className={styles.workspaceMain}>
            {activeTab === 'overview' ? (
              <SimpleWorkspaceOverview
                model={{
                  snapshot,
                  profile,
                  memberDisplayName,
                  memberPhone,
                  memberBasicValues,
                  additionalProfileFields,
                  latestMonth,
                  previousMonth,
                  monthlyTrainingSummary,
                  evaluatedCapabilityCount,
                  capabilityAccounts,
                  communicationLoading,
                  communicationRows,
                  communicationSourceCounts,
                  currentRecommendation,
                  currentProblems,
                  unavailableMonthOffsets,
                  monthlyProgress,
                  monthlyProgressLoading,
                  monthlyTrainingTrend,
                  hasWeeklyTraining,
                  weeklyTrainingData,
                  weeklyTrainingSummary,
                  bodyMetricTrends,
                  weightTrendData,
                  circumferenceTrendData,
                  radarData,
                  timelineLoading,
                  timelineEvidence,
                  availableTrainingContextCards,
                }}
                actions={{ changeActiveTab, openTimelineEvent }}
              />
            ) : (
              <SimpleWorkspaceTabs
                model={{
                  activeTab,
                  memberId: snapshot.memberId,
                  actionLoading,
                  actionRecords,
                  actionPage,
                  actionProfilesLoading,
                  actionProfiles,
                  actionProfileCurrent,
                  courseLoading,
                  courseRecords,
                  courseCurrent,
                  coursePage,
                  communicationLoading,
                  communicationRows,
                  communicationCurrent,
                  communicationEvidenceCount: communicationEvidence.length,
                  communicationPage,
                  capabilityAccounts,
                  timelineLoading,
                  timelineEvidence,
                  timelinePage,
                }}
                actions={{
                  changeActiveTab,
                  loadActionPage,
                  setActionProfileCurrent,
                  changeCoursePage,
                  changeCommunicationPage,
                  openTimelineEvent,
                  loadMoreTimeline: () =>
                    loadTimelinePage(
                      snapshot.memberId,
                      true,
                      workspaceRequestSequence.current,
                    ),
                }}
                columns={workspaceColumns}
              />
            )}
          </main>
        </div>
      )}

      <SimpleWorkspaceDrawers
        model={{
          actionNames,
          actionProfileVisible,
          selectedActionProfile,
          actionMetricLoading,
          selectedMetricSeries,
          actionMetricChart,
          actionMetricTrend,
          capabilityHistoryVisible,
          capabilityHistoryTitle,
          capabilityHistoryChart,
          capabilityHistoryLoading,
          capabilityHistory,
          timelineDetailVisible,
          selectedTimelineEvent,
          timelineDetailLoading,
          timelineDetail,
        }}
        actions={{
          closeActionProfile,
          closeCapabilityHistory,
          closeTimelineDetail,
          openCourseDetail,
          openActionDetail,
        }}
        columns={workspaceColumns}
      />
    </Space>
  );
}
